import { ReactNode, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/utils/cn';
import { Button } from './Button';
import { Icon } from './Icon';
import { ICONS } from '@/constants/icons';
import { CancelButton } from '@/components/ui/CancelButton';
import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';

/**
 * ConfirmDialog Component
 *
 * A visually branded confirmation dialog using Radix Dialog
 * Features:
 * - Gradient header based on variant
 * - Animated icon with subtle bounce
 * - Backdrop blur effect
 * - Smooth open/close animations
 * - Mobile responsive (90vw max)
 * - WCAG 2.1 AA compliant
 */

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Plain text, or a ReactNode for richer content (e.g. a warning list). */
  description: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'default';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  /** Show loading state on confirm button */
  loading?: boolean;
  /** Parent owns closing after an asynchronous action succeeds. */
  closeOnConfirm?: boolean;
}

export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
  loading = false,
  closeOnConfirm = true,
}: ConfirmDialogProps) => {
  const safeActionRef = useRef<HTMLButtonElement>(null);
  const confirmingRef = useRef(false);
  const [confirming, setConfirming] = useState(false);
  const busy = loading || confirming;
  useEffect(() => {
    if (!open) return;
    const closeFocusedConfirm = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || busy) return;
      const dialog = safeActionRef.current?.closest('[role="dialog"]');
      if (!dialog?.contains(document.activeElement)) return;
      // A parent drawer has its own Radix Escape layer. Capture the key while
      // focus is in this confirmation so it closes in one press, before the parent.
      event.preventDefault();
      event.stopImmediatePropagation();
      onOpenChange(false);
    };
    document.addEventListener('keydown', closeFocusedConfirm, true);
    return () => document.removeEventListener('keydown', closeFocusedConfirm, true);
  }, [open, busy, onOpenChange]);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && busy) return;
    onOpenChange(nextOpen);
  };

  const handleConfirm = async () => {
    if (confirmingRef.current || loading) return;
    confirmingRef.current = true;
    setConfirming(true);
    try {
      await onConfirm();
      if (closeOnConfirm) onOpenChange(false);
    } catch {
      // Keep the dialog open; the action owns its failure feedback.
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
    }
  };

  const handleCancel = () => {
    if (busy) return;
    onCancel?.();
    onOpenChange(false);
  };

  // Configuration for each variant
  const variantConfig = {
    danger: {
      icon: ICONS.TRASH,
      headerGradient: 'bg-gradient-to-r from-red-500 via-red-500 to-rose-600',
      iconBg: 'bg-white/20',
      buttonVariant: 'danger' as const,
      textColor: 'text-white',
    },
    warning: {
      icon: ICONS.WARNING,
      headerGradient: 'bg-gradient-coral',
      iconBg: 'bg-white/20',
      buttonVariant: 'primary' as const,
      textColor: 'text-gray-900',
    },
    default: {
      icon: ICONS.INFO,
      headerGradient: 'bg-gradient-mint',
      iconBg: 'bg-white/20',
      buttonVariant: 'primary' as const,
      textColor: 'text-gray-900',
    },
  };

  const config = variantConfig[variant];
  const stackZ = getStackZIndex(useModalStackDepth(open));

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Backdrop with blur effect */}
        <Dialog.Overlay
          style={{ zIndex: stackZ.backdrop }}
          className={cn(
            'fixed inset-0',
            'bg-black/50 backdrop-blur-sm',
            // Animations
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out',
            'duration-200'
          )}
        />

        {/* Dialog Content */}
        <Dialog.Content
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            safeActionRef.current?.focus();
          }}
          style={{ zIndex: stackZ.content }}
          className={cn(
            // Positioning
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            // Sizing - responsive
            'w-[90vw] max-w-md',
            // Max height
            'max-h-[90vh]',
            // Styling
            'bg-white rounded-2xl shadow-2xl',
            'overflow-hidden',
            // Focus
            'focus:outline-none',
            // Layout - flex container
            'flex flex-col',
            // Open/close animations - use fade to avoid transform conflicts with positioning
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out'
          )}
        >
          {/* Fixed Header - Gradient */}
          <div
            className={cn(
              'flex-shrink-0',
              'px-6 pt-8 pb-6',
              config.headerGradient,
              'relative overflow-hidden'
            )}
          >
            {/* Icon in frosted glass circle */}
            <div className="relative z-10 flex flex-col items-center">
              <div
                className={cn(
                  'w-16 h-16',
                  config.iconBg,
                  'backdrop-blur-sm',
                  'rounded-full',
                  'flex items-center justify-center',
                  'mb-4',
                  'shadow-lg shadow-black/10',
                  'border border-white/20',
                  // Subtle animation
                  'animate-bounce-subtle'
                )}
              >
                <Icon
                  name={config.icon}
                  size="lg"
                  className={cn("drop-shadow-sm", variant === 'danger' ? 'text-white' : 'text-secondary-500')}
                />
              </div>

              {/* Title */}
              <Dialog.Title className={cn("text-base sm:text-lg md:text-xl font-bold text-center drop-shadow-sm", config.textColor)}>
                {title}
              </Dialog.Title>
            </div>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {/* Description — rendered as a div (not Radix's default <p>) since
                description may be block-level ReactNode (e.g. a warning list) */}
            <Dialog.Description asChild>
              <div className="text-base text-gray-600 text-center leading-relaxed">
                {description}
              </div>
            </Dialog.Description>
          </div>

          {/* Fixed Footer - Action Buttons */}
          <div className="flex-shrink-0 px-6 pb-6 flex justify-end gap-1.5 sm:gap-2">
            <CancelButton
              ref={safeActionRef}
              onClick={handleCancel}
              disabled={busy}
              tooltip={cancelText}
            />
            <Button
              variant={config.buttonVariant}
              onClick={handleConfirm}
              loading={busy}
              className={cn(
                'transition-all duration-200',
                variant === 'danger' && 'bg-red-500 hover:bg-red-600',
                variant === 'warning' && 'bg-amber-500 hover:bg-amber-600'
              )}
            >
              {confirmText}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/**
 * ConfirmDialogSimple - A simpler variant without the gradient header
 * Use this for less critical confirmations
 */
interface ConfirmDialogSimpleProps extends Omit<ConfirmDialogProps, 'variant'> {
  variant?: 'danger' | 'warning' | 'default';
  /** Whether to show the icon badge. Defaults to true */
  showIcon?: boolean;
}

export const ConfirmDialogSimple = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  loading = false,
  showIcon = true,
}: ConfirmDialogSimpleProps) => {
  const stackDepth = useModalStackDepth(open);
  const { backdrop: backdropZ, content: contentZ } = getStackZIndex(stackDepth);
  const handleConfirm = () => {
    onConfirm();
    if (!loading) {
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const iconConfig = {
    danger: {
      icon: 'recycle-bin',
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      borderColor: 'border-red-200',
    },
    warning: {
      icon: ICONS.WARNING,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
      borderColor: 'border-amber-200',
    },
    default: {
      icon: ICONS.INFO,
      bgColor: 'bg-primary-100',
      iconColor: 'text-primary-600',
      borderColor: 'border-primary-200',
    },
  };

  const config = iconConfig[variant];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay
          style={{ zIndex: backdropZ }}
          className={cn(
            'fixed inset-0 bg-black/50 backdrop-blur-sm',
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out',
            'duration-200'
          )}
        />

        {/* Dialog Content */}
        <Dialog.Content
          style={{ zIndex: contentZ }}
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[90vw] max-w-md',
            'max-h-[90vh]',
            'bg-white rounded-2xl shadow-2xl',
            'focus:outline-none',
            'flex flex-col',
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out'
          )}
        >
          {/* Fixed Header - Icon and Title */}
          <div className="flex-shrink-0 p-6 pb-4">
            {/* Icon */}
            {showIcon && (
              <div
                className={cn(
                  'mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4',
                  'border-2',
                  config.bgColor,
                  config.borderColor
                )}
              >
                <Icon name={config.icon} size="sm" className={config.iconColor} />
              </div>
            )}

            {/* Title */}
            <Dialog.Title className="text-lg font-semibold text-gray-900 text-center">
              {title}
            </Dialog.Title>
          </div>

          {/* Scrollable Body */}
          {description ? (
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              {/* Description — rendered as a div (not Radix's default <p>) since
                  description may be block-level ReactNode */}
              <Dialog.Description asChild>
                <div className="text-sm text-gray-600 text-center leading-relaxed">
                  {description}
                </div>
              </Dialog.Description>
            </div>
          ) : (
            <VisuallyHidden asChild>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <Dialog.Description>{title} confirmation dialog</Dialog.Description>
            </VisuallyHidden>
          )}

          {/* Fixed Footer - Actions */}
          <div className="flex-shrink-0 px-6 pb-6 flex justify-end gap-1.5 sm:gap-2">
            <CancelButton
              onClick={handleCancel}
              disabled={loading}
              tooltip={cancelText}
            />
            <Button
              variant={variant === 'danger' ? 'danger' : 'primary'}
              onClick={handleConfirm}
              loading={loading}
            >
              {confirmText}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
